import React, { createContext, useReducer, useContext, useRef } from 'react';
import { generateWorkout } from '../data/exerciseData';

// Initial state for the workout
const initialState = {
    // Selection state
    selectedMuscleGroups: '',
    repCount: 10,
    setCount: 1,
    difficultyLevel: 4,

    // Generated workout state
    generatedWorkout: [],
    currentExerciseIndex: 0,

    // Progress tracking
    totalCompletedReps: 0,
    totalRemainingReps: 0,

    // Individual exercise rep tracking
    exerciseReps: {},

    // Workout status
    workoutStarted: false,
    workoutComplete: false
};

// Action types
const actions = {
    SET_MUSCLE_GROUPS: 'SET_MUSCLE_GROUPS',
    SET_REP_COUNT: 'SET_REP_COUNT',
    SET_SET_COUNT: 'SET_SET_COUNT',
    SET_DIFFICULTY_LEVEL: 'SET_DIFFICULTY_LEVEL',
    GENERATE_WORKOUT: 'GENERATE_WORKOUT',
    SET_CURRENT_EXERCISE: 'SET_CURRENT_EXERCISE',
    NEXT_EXERCISE: 'NEXT_EXERCISE',
    PREVIOUS_EXERCISE: 'PREVIOUS_EXERCISE',
    INCREMENT_COMPLETED_REPS: 'INCREMENT_COMPLETED_REPS',
    DECREMENT_REMAINING_REPS: 'DECREMENT_REMAINING_REPS',
    UPDATE_EXERCISE_REPS: 'UPDATE_EXERCISE_REPS',
    START_WORKOUT: 'START_WORKOUT',
    COMPLETE_WORKOUT: 'COMPLETE_WORKOUT',
    RESET_WORKOUT: 'RESET_WORKOUT',
    CLEAR_WORKOUT_PROGRESS: 'CLEAR_WORKOUT_PROGRESS'
};

// Generate initial exercise reps object based on workout and rep count
function generateExerciseReps(workout, repCount) {
    const reps = {};
    workout.forEach(exercise => {
        reps[exercise] = {
            totalReps: repCount,
            remainingReps: repCount,
            completedReps: 0
        };
    });
    return reps;
}

// Reducer function to handle state updates
function workoutReducer(state, action) {
    switch (action.type) {
        case actions.SET_MUSCLE_GROUPS:
            return {
                ...state,
                selectedMuscleGroups: action.payload
            };

        case actions.SET_REP_COUNT:
            return {
                ...state,
                repCount: parseInt(action.payload)
            };

        case actions.SET_SET_COUNT:
            return {
                ...state,
                setCount: parseInt(action.payload)
            };

        case actions.SET_DIFFICULTY_LEVEL:
            return {
                ...state,
                difficultyLevel: parseInt(action.payload)
            };

        case actions.GENERATE_WORKOUT: {
            const generatedWorkout = action.payload || generateWorkout(state.selectedMuscleGroups);
            const totalReps = generatedWorkout.length * state.repCount * state.setCount;
            const exerciseReps = generateExerciseReps(generatedWorkout, state.repCount);

            return {
                ...state,
                generatedWorkout,
                totalRemainingReps: totalReps,
                exerciseReps
            };
        }

        case actions.SET_CURRENT_EXERCISE:
            return {
                ...state,
                currentExerciseIndex: action.payload
            };

        case actions.NEXT_EXERCISE: {
            const nextIndex = state.currentExerciseIndex + 1;
            return {
                ...state,
                currentExerciseIndex: nextIndex < state.generatedWorkout.length
                    ? nextIndex
                    : state.currentExerciseIndex
            };
        }

        case actions.PREVIOUS_EXERCISE: {
            const prevIndex = state.currentExerciseIndex - 1;
            return {
                ...state,
                currentExerciseIndex: prevIndex >= 0
                    ? prevIndex
                    : 0
            };
        }

        case actions.INCREMENT_COMPLETED_REPS:
            return {
                ...state,
                totalCompletedReps: state.totalCompletedReps + action.payload
            };

        case actions.DECREMENT_REMAINING_REPS:
            return {
                ...state,
                totalRemainingReps: Math.max(0, state.totalRemainingReps - action.payload)
            };

        case actions.UPDATE_EXERCISE_REPS: {
            const { exercise, repsCompleted } = action.payload;
            const currentExerciseReps = state.exerciseReps[exercise];

            if (!currentExerciseReps) return state;

            const updatedReps = {
                ...currentExerciseReps,
                remainingReps: Math.max(0, currentExerciseReps.remainingReps - repsCompleted),
                completedReps: currentExerciseReps.completedReps + repsCompleted
            };

            return {
                ...state,
                exerciseReps: {
                    ...state.exerciseReps,
                    [exercise]: updatedReps
                },
                totalCompletedReps: state.totalCompletedReps + repsCompleted,
                totalRemainingReps: Math.max(0, state.totalRemainingReps - repsCompleted)
            };
        }

        case actions.START_WORKOUT:
            return {
                ...state,
                workoutStarted: true
            };

        case actions.COMPLETE_WORKOUT:
            return {
                ...state,
                workoutComplete: true
            };

        case actions.RESET_WORKOUT:
            return {
                ...initialState,
                selectedMuscleGroups: state.selectedMuscleGroups,
                repCount: state.repCount,
                setCount: state.setCount,
                difficultyLevel: state.difficultyLevel
            };

        case actions.CLEAR_WORKOUT_PROGRESS:
            return {
                ...state,
                totalCompletedReps: 0,
                totalRemainingReps: 0,
                currentExerciseIndex: 0,
                exerciseReps: generateExerciseReps(state.generatedWorkout, state.repCount),
                workoutStarted: false,
                workoutComplete: false
            };

        default:
            return state;
    }
}

// Create context
const WorkoutContext = createContext();

// Provider component
export function WorkoutProvider({ children }) {
    const [state, dispatch] = useReducer(workoutReducer, initialState);
    const isInitialMount = useRef(true);

    // Save state to localStorage only when specifically needed (not on every state change)
    const saveToLocalStorage = (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.warn('Failed to save to localStorage:', error);
        }
    };

    // Save specific state changes individually instead of using useEffect
    const setMuscleGroups = (muscleGroups) => {
        dispatch({ type: actions.SET_MUSCLE_GROUPS, payload: muscleGroups });
        saveToLocalStorage('selectedMuscleGroups', muscleGroups);
    };

    const setRepCount = (repCount) => {
        dispatch({ type: actions.SET_REP_COUNT, payload: repCount });
        saveToLocalStorage('repCount', repCount);
    };

    const setSetCount = (setCount) => {
        dispatch({ type: actions.SET_SET_COUNT, payload: setCount });
        saveToLocalStorage('setCount', setCount);
    };

    const setDifficultyLevel = (level) => {
        dispatch({ type: actions.SET_DIFFICULTY_LEVEL, payload: level });
        saveToLocalStorage('difficultyLevel', level);
    };

    const generateWorkoutAction = (customWorkout) => {
        dispatch({ type: actions.GENERATE_WORKOUT, payload: customWorkout });
    };

    const nextExercise = () => {
        dispatch({ type: actions.NEXT_EXERCISE });
    };

    const previousExercise = () => {
        dispatch({ type: actions.PREVIOUS_EXERCISE });
    };

    const completeReps = (exercise, reps) => {
        dispatch({
            type: actions.UPDATE_EXERCISE_REPS,
            payload: { exercise, repsCompleted: reps }
        });
    };

    const startWorkout = () => {
        dispatch({ type: actions.START_WORKOUT });
    };

    const completeWorkout = () => {
        dispatch({ type: actions.COMPLETE_WORKOUT });
    };

    const resetWorkout = () => {
        dispatch({ type: actions.RESET_WORKOUT });
    };

    const clearWorkoutProgress = () => {
        dispatch({ type: actions.CLEAR_WORKOUT_PROGRESS });
    };

    // Computed values
    const currentExercise = state.generatedWorkout[state.currentExerciseIndex] || '';
    const isLastExercise = state.currentExerciseIndex === state.generatedWorkout.length - 1;
    const isFirstExercise = state.currentExerciseIndex === 0;

    // Determine the output level based on rep count
    const getRepCountLevel = () => {
        const repCount = state.repCount;
        if (repCount >= 0 && repCount <= 7) return "Max strength";
        if (repCount >= 8 && repCount <= 17) return "Challenge level";
        if (repCount >= 18 && repCount <= 26) return "Strength with a touch of endurance";
        if (repCount >= 27 && repCount <= 35) return "Ideal strength to endurance ratio";
        if (repCount >= 36 && repCount <= 43) return "Max endurance";
        return "Outside of normal rep range for 1 set, consider less reps for the first set";
    };

    const repCountLevel = getRepCountLevel();

    // Determine workout difficulty description
    const getDifficultyDescription = () => {
        switch (state.difficultyLevel) {
            case 1: return "Beginner (25% Intensity)";
            case 2: return "Intermediate (50% Intensity)";
            case 3: return "Advanced (75% Intensity)";
            default: return "Expert (100% Intensity)";
        }
    };

    const difficultyDescription = getDifficultyDescription();

    return (
        <WorkoutContext.Provider value={{
            state,
            actions: {
                setMuscleGroups,
                setRepCount,
                setSetCount,
                setDifficultyLevel,
                generateWorkout: generateWorkoutAction,
                nextExercise,
                previousExercise,
                completeReps,
                startWorkout,
                completeWorkout,
                resetWorkout,
                clearWorkoutProgress
            },
            computed: {
                currentExercise,
                isLastExercise,
                isFirstExercise,
                repCountLevel,
                difficultyDescription
            }
        }}>
            {children}
        </WorkoutContext.Provider>
    );
}

export function useWorkout() {
    const context = useContext(WorkoutContext);

    if (!context) {
        throw new Error('useWorkout must be used within a WorkoutProvider');
    }

    return context;
}

// Add this line to export the context itself
export { WorkoutContext };